const defaultChartConfig = {
  data: {},
  options: {
    scales: {
      x: {
        beginAtZero: true,
        title: {
          display: true,
        },
        offset: true,
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
        },
        ticks: {
          callback: function (value) {
            return value.toLocaleString();
          },
        },
      },
    },
    interaction: {
      mode: "index",
      axis: "x",
      intersect: true,
    },
    plugins: {
      tooltip: {
        mode: "nearest",
        intersect: false,
        callbacks: {
          label: function (context) {
            let label = context.dataset.label || "";
            if (label) {
              label += ": ";
            }
            label += context.raw.toLocaleString();
            return label;
          },
        },
      },
    },
    hover: {
      mode: "index",
      intersect: true,
    },
    responsive: true,
  },
};

const BY_CHART_MAP = {
  cha_operators: "Well Operators",
  cha_state: "State",
  cha_county: "County",
};

function sortChartDataDescending(period) {
  const pairs = period.categories.map((category, index) => ({
    category,
    value: period.data[index],
  }));

  pairs.sort((a, b) => b.value - a.value);

  return {
    ...period,
    categories: pairs.map((pair) => pair.category),
    data: pairs.map((pair) => pair.value),
  };
}

class RigsData extends HTMLElement {
  constructor() {
    super();
    this.id = this.getAttribute("data-id");
    this.apiUrl = "https://api.rextag.com/rigs-statistics";
    this.rigsData = {};
    this.charts = {};

    this.getRigsData();
  }

  async getRigsData() {
    this.setAllChartsLoading(true);

    try {
      const response = await fetch(this.apiUrl, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Response status: ${response.status}`);
      }

      const json = await response.json();
      this.processData(json);
      this.bindPeriodSelects();
      this.renderCharts();
    } catch (error) {
      console.error(error.message);
      this.setAllChartsError("Unable to load chart data.");
    }
  }

  setChartLoading(container, isLoading) {
    if (!container) return;

    const select = container.querySelector("[data-period-select]");

    container.classList.toggle("is-loading", isLoading);
    container.classList.remove("is-error");

    if (select) {
      select.disabled = isLoading || container.classList.contains("is-error");
    }
  }

  setChartError(container, message) {
    if (!container) return;

    const select = container.querySelector("[data-period-select]");
    const loadingText = container.querySelector(
      ".rigs-page-data-chart-loading-text"
    );

    container.classList.remove("is-loading");
    container.classList.add("is-error");

    if (loadingText) {
      loadingText.textContent = message;
    }

    if (select) {
      select.disabled = true;
    }
  }

  setAllChartsLoading(isLoading) {
    this.querySelectorAll(".rigs-page-data-chart-wrapper").forEach((wrapper) => {
      this.setChartLoading(wrapper, isLoading);
    });
  }

  setAllChartsError(message) {
    this.querySelectorAll(".rigs-page-data-chart-wrapper").forEach((wrapper) => {
      this.setChartError(wrapper, message);
    });
  }

  processData(data) {
    this.rigsData.cha_rigs = {};

    data.chart.forEach((period) => {
      if (period.id === "weekly" || period.id === "monthly") {
        this.rigsData.cha_rigs[period.id] = period;
      }
    });

    Object.entries(BY_CHART_MAP).forEach(([chartId, groupName]) => {
      const group = data.by.find((item) => item.name === groupName);
      this.rigsData[chartId] = {};

      if (!group) return;

      group.data.forEach((period) => {
        this.rigsData[chartId][period.id] = sortChartDataDescending(period);
      });
    });
  }

  bindPeriodSelects() {
    this.querySelectorAll("[data-period-select]").forEach((select) => {
      select.addEventListener("change", (event) => {
        const wrapper = event.target.closest(".rigs-page-data-chart-wrapper");
        const chartId = wrapper.getAttribute("data-id");
        this.setChartLoading(wrapper, true);
        this.updateChart(chartId, event.target.value);
      });
    });
  }

  renderCharts() {
    this.querySelectorAll(".rigs-page-data-chart-wrapper").forEach((wrapper) => {
      const chartId = wrapper.getAttribute("data-id");
      const periodId = wrapper.querySelector("[data-period-select]").value;
      this.updateChart(chartId, periodId);
    });
  }

  updateChart(chartId, periodId) {
    const container = this.querySelector('div[data-id="' + chartId + '"]');
    if (!container || !this.rigsData[chartId]?.[periodId]) {
      if (container) {
        this.setChartError(container, "No chart data available.");
      }
      return;
    }

    const chartData =
      chartId === "cha_rigs"
        ? this.processChartDataTimeSeries(chartId, periodId, container)
        : this.processChartDataGrouped(chartId, periodId, container);

    const xTitle = chartId === "cha_rigs" ? "Date" : "";
    this.generateChart(chartId, chartData, xTitle, "Rigs/Frac Crews", container);
    this.setChartLoading(container, false);
  }

  generateChart(chartId, chartData, xTitle, yTitle, container) {
    if (!container) return;

    if (this.charts[chartId]) {
      this.charts[chartId].destroy();
    }

    const chartType = container.getAttribute("data-chart-type");
    const context = container.querySelector("canvas").getContext("2d");
    const chartConfig = { ...defaultChartConfig };

    chartConfig.type = chartType;
    chartConfig.options.scales.x.title.text = xTitle;
    chartConfig.options.scales.y.title.text = yTitle;
    chartConfig.data = { ...chartData };

    this.charts[chartId] = new Chart(context, { ...chartConfig });
  }

  processChartDataTimeSeries(chartId, periodId, container) {
    const data = this.rigsData[chartId][periodId];
    const ctx = container.querySelector("canvas").getContext("2d");

    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "rgba(0, 123, 255, 0.8)");
    gradient.addColorStop(1, "rgba(0, 123, 255, 0.1)");

    return {
      labels: data.categories,
      datasets: [
        {
          label: "Rigs/Frac Crews",
          data: data.data,
          borderColor: "#007bff",
          backgroundColor: gradient,
          borderWidth: 2,
          fill: true,
          pointRadius: 3,
          pointHoverRadius: 6,
          tension: 0.3,
        },
      ],
    };
  }

  processChartDataGrouped(chartId, periodId, container) {
    const data = this.rigsData[chartId][periodId];
    const ctx = container.querySelector("canvas").getContext("2d");

    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, "rgba(0, 123, 255, 1)");
    gradient.addColorStop(1, "rgba(0, 123, 255, 0.6)");

    return {
      labels: data.categories,
      datasets: [
        {
          label: "Rigs/Frac Crews",
          data: data.data,
          backgroundColor: gradient,
          borderColor: "#007bff",
          borderWidth: 1,
          pointRadius: 5,
        },
      ],
    };
  }
}

customElements.define("rigs-data", RigsData);
